import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { LoggerService } from '../../logger/logger.service';
import { TokenResponse, UserInfoResponse, UserData } from '@nationwide/dgs-angular-billing-common';
import jwtDecode from 'jwt-decode';
import { CredentialManagerService } from '../../session/identifiers/credential-management.service';

export type { TokenResponse, UserInfoResponse, UserData };

const MILLISECONDS_IN_SECOND = 1000;
const PKCE_STATE_LENGTH = 32;

@Injectable({
    providedIn: 'root'
})
export class EUAv3PKCESharedService {
    private jwtDecodeService = jwtDecode;

    constructor(
        private httpClient: HttpClient,
        private logger: LoggerService,
        private credentialManagerService: CredentialManagerService
    ) { }

    generatePKCEVerifier(): string {
        const array = new Uint8Array(32);
        crypto.getRandomValues(array);
        return btoa(String.fromCharCode(...Array.from(array)))
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=/g, '');
    }

    async generatePKCEChallenge(verifier: string): Promise<string> {
        const encoder = new TextEncoder();
        const data = encoder.encode(verifier);
        const hash = await crypto.subtle.digest('SHA-256', data);
        return this.bufferToBase64Url(hash);
    }

    generateState(): string {
        const array = new Uint8Array(32);
        crypto.getRandomValues(array);
        return Array.from(array, byte => String.fromCharCode(byte))
            .map(c => {
                const code = c.charCodeAt(0);
                return (code % 64).toString(16);
            })
            .join('')
            .substring(0, PKCE_STATE_LENGTH);
    }

    exchangeCodeForToken(code: string, verifier: string, redirectUri: string): Observable<TokenResponse> {
        const body = new HttpParams()
            .set('grant_type', 'authorization_code')
            .set('code', code)
            .set('client_id', environment.EUAV3_CONFIG.clientId)
            .set('redirect_uri', redirectUri)
            .set('code_verifier', verifier);

        return this.httpClient.post<TokenResponse>(
            environment.EUAV3_CONFIG.tokenEndpoint,
            body.toString(),
            { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
        ).pipe(
            tap(() => {
                this.logger.info('Token exchange successful');
            }),
            catchError((error) => {
                this.logger.error('Token exchange failed', error);
                return throwError(() => error);
            })
        );
    }

    refreshToken(refreshToken: string): Observable<TokenResponse> {
        const body = new HttpParams()
            .set('grant_type', 'refresh_token')
            .set('refresh_token', refreshToken)
            .set('client_id', environment.EUAV3_CONFIG.clientId);

        return this.httpClient.post<TokenResponse>(
            environment.EUAV3_CONFIG.refreshEndpoint,
            body.toString(),
            { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
        ).pipe(
            tap((response) => {
                this.logger.info('Token refresh successful');
                this.storeTokenResponse(response);
            }),
            catchError((error) => {
                this.logger.error('Token refresh failed', error);
                return throwError(() => error);
            })
        );
    }

    fetchUserInfo(accessToken: string): Observable<UserInfoResponse> {
        return this.httpClient.get<UserInfoResponse>(
            environment.EUAV3_CONFIG.userInfoEndpoint,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`
                }
            }
        ).pipe(
            tap(() => {
                this.logger.info('User info fetched successfully');
            }),
            catchError((error) => {
                this.logger.error('Failed to fetch user info', error);
                return throwError(() => error);
            })
        );
    }

    storeTokenData(tokens: TokenResponse, userInfo: UserInfoResponse): void {
        const expirationTime = new Date().getTime() + (tokens.expires_in * MILLISECONDS_IN_SECOND);
        sessionStorage.setItem('access_token', tokens.access_token);
        sessionStorage.setItem('tokenDetails', `${tokens.access_token}, ${expirationTime}, ${tokens.expires_in}`);
        sessionStorage.setItem('copyTokenDetails', `${tokens.access_token}, ${expirationTime}, ${tokens.expires_in}`);

        if (tokens.id_token) {
            sessionStorage.setItem('id_token', tokens.id_token);
            sessionStorage.setItem('jsonWebToken', tokens.id_token);
        }

        if (tokens.refresh_token) {
            sessionStorage.setItem('refresh_token', tokens.refresh_token);
        }

        const userData = this.extractUserData(userInfo);
        sessionStorage.setItem('ecn', userData.ecn || '');
        if (userData.userId) {
            sessionStorage.setItem('UserName', userData.userId);
        }
        if (userData.guid) {
            sessionStorage.setItem('guid', userData.guid);
        }
        if (userData.firstName) {
            sessionStorage.setItem('firstName', userData.firstName);
        }
        if (userData.email) {
            sessionStorage.setItem('email', userData.email);
        }
        if (userData.permission) {
            sessionStorage.setItem('permission', userData.permission);
        }
        if (Array.isArray(userInfo.agreements)) {
            sessionStorage.setItem('eua3Agreements', JSON.stringify(userInfo.agreements));
        }
    }

    extractUserDataFromToken(idToken: string): UserData {
        try {
            const decoded = this.jwtDecodeService(idToken) as any;
            return this.extractUserData(decoded);
        } catch (error) {
            const loggerPayload: object = error instanceof Error ? error : { error };
            this.logger.error('Failed to decode ID token', loggerPayload);
            return { ecn: '' };
        }
    }

    private extractUserData(data: any): UserData {
        const ecn = this.resolveEcn(data);

        const enrichedUserInfo = {
            ...data,
            ecn: ecn || '',
            ...(data.given_name || data.firstName ? {
                given_name: data.given_name ?? data.firstName
            } : {}),
            ...(data.family_name || data.lastName ? {
                family_name: data.family_name ?? data.lastName
            } : {}),
            ...(data.email || data.emailAddress ? {
                email: data.email ?? data.emailAddress
            } : {})
        };

        const userData = {
            ecn: ecn || '',
            userId: data.userId ?? data.preferred_username ?? data.sub,
            guid: data.guid,
            firstName: data.given_name ?? data.firstName,
            email: data.email,
            permission: Array.isArray(data.permission) ? data.permission.join(',') : data.permission
        };

        this.credentialManagerService.userInfo = enrichedUserInfo;

        return userData;
    }

    private resolveEcn(data: any): string {
        const candidates = [data.ecn, this.ecnFromSub(data.sub)];
        const numericEcn = candidates.find((candidate) => typeof candidate === 'string' && /^\d+$/.test(candidate));

        if (!numericEcn) {
            this.logger.warn('No numeric ecn found in user info');
            return '';
        }

        return numericEcn;
    }

    private ecnFromSub(sub: string): string {
        if (!sub) {
            return '';
        }

        const match = sub.match(/unidentified(.enterpriseCustomerNumber)?:(.*)/);
        if (match) {
            return match[2];
        }

        return sub.split(':')[1] ?? sub;
    }

    private bufferToBase64Url(buffer: ArrayBuffer): string {
        const bytes = new Uint8Array(buffer);
        let binary = '';
        for (let i = 0; i < bytes.byteLength; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        const base64 = btoa(binary)
            .replace(/\+/g, '-')
            .replace(/\//g, '_');

        let endIndex = base64.length;
        while (endIndex > 0 && base64.charAt(endIndex - 1) === '=') {
            endIndex--;
        }

        return endIndex === base64.length ? base64 : base64.slice(0, endIndex);
    }

    private storeTokenResponse(tokens: TokenResponse): void {
        const expirationTime = new Date().getTime() + (tokens.expires_in * MILLISECONDS_IN_SECOND);
        sessionStorage.setItem('access_token', tokens.access_token);
        sessionStorage.setItem('tokenDetails', `${tokens.access_token}, ${expirationTime}, ${tokens.expires_in}`);
        sessionStorage.setItem('copyTokenDetails', `${tokens.access_token}, ${expirationTime}, ${tokens.expires_in}`);

        if (tokens.id_token) {
            sessionStorage.setItem('jsonWebToken', tokens.id_token);
            sessionStorage.setItem('id_token', tokens.id_token);
        }

        if (tokens.refresh_token) {
            sessionStorage.setItem('refresh_token', tokens.refresh_token);
        }
    }
}
